<script setup>
/**
 * One broadcast, while it is going out and after it has. THE PLAN, §4 P10.
 *
 * IT POLLS, AND IT STOPS. A running send moves on its own, so a page that only tells the
 * truth when you press Refresh is a page that lies for as long as you look at it. It
 * re-reads every five seconds WHILE the status is one that can still change (`running`,
 * `paused`) and never otherwise — a finished broadcast is finished, and polling it forever
 * is a request per five seconds per open tab for a number that will not move. The interval
 * is cleared on unmount AND on deactivation, because this page lives inside
 * `CampaignsPageRouteView`'s `<keep-alive>` and is therefore never unmounted by ordinary
 * navigation.
 *
 * THE THREE ACTIONS ARE GATED ON THE STATUSES THE SIDECAR WILL ACTUALLY ACCEPT, and hidden
 * rather than disabled otherwise — a disabled button invites a click whose only possible
 * outcome is a 409 that reads like a bug:
 *
 *   • **Stop it** — running or paused. `cancel` explicitly accepts `paused`, and without
 *     that a paused broadcast could never be retired at all: `finalise` requires `running`,
 *     so maintenance would reconcile it forever.
 *   • **Start it again** — paused only. It is the only other way out of that status.
 *   • **Send again to the ones it missed** — running, or finished WITH at least one that
 *     did not arrive. Finished matters most: one maintenance tick reaps an abandoned
 *     recipient and then finalises the run, so the failures most worth retrying are usually
 *     on a broadcast that has already completed.
 *
 *     The `failed > 0` half is an APPROXIMATION and cannot be more: nothing in either shape
 *     distinguishes a recipient that never reached WhatsApp (re-sendable) from one whose
 *     delivery callback failed after the message was billed (never re-sendable). The
 *     sidecar makes that call itself and 409s if nothing qualifies — so this gate must
 *     never be read as a promise that the retry will do anything.
 *
 *     THAT IS WHY THE BUTTON STAYS AND THE REFUSAL GOT A SENTENCE. Hiding it on a
 *     stricter guess is the worse trade in both directions: there is no retryable count
 *     on the DTO to guess from, and a run whose failures genuinely ARE re-sendable would
 *     lose its only remedy. So the copy carries the uncertainty instead — the question
 *     says "up to", and `NOTHING_TO_RETRY` turns the sidecar's refusal into a sentence
 *     about money rather than a clause about recipients.
 *
 * EVERY ACTION ASKS FIRST, IN PLACE. Retrying spends money per recipient and stopping
 * cannot be undone; the question replaces the button where it stood, so the numbers it is
 * about are still on screen above it.
 */
import {
  computed,
  onActivated,
  onDeactivated,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from 'vue';
import { useRoute } from 'vue-router';

import Button from 'dashboard/components-next/button/Button.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';

import BroadcastStatusChip from 'dashboard/routes/dashboard/wasloai/components/broadcast/BroadcastStatusChip.vue';
import ProgressBreakdown from 'dashboard/routes/dashboard/wasloai/components/broadcast/ProgressBreakdown.vue';
import StatTile from 'dashboard/routes/dashboard/wasloai/components/StatTile.vue';
import WasloPageLayout from 'dashboard/routes/dashboard/wasloai/components/WasloPageLayout.vue';
import { useWasloI18n } from 'dashboard/routes/dashboard/wasloai/i18n.js';
import { useWasloApi } from 'dashboard/routes/dashboard/wasloai/composables/useWasloApi.js';
import { useWasloAccountId } from 'dashboard/routes/dashboard/wasloai/composables/useWasloAccountId.js';
import { toIntlLocale } from 'dashboard/routes/dashboard/wasloai/helpers/intl.js';
import {
  MARKER,
  isolateParts,
} from 'dashboard/routes/dashboard/wasloai/helpers/bidi.js';
import {
  WASLO_ERROR_CODES,
  wasloApi,
} from 'dashboard/routes/dashboard/wasloai/api/client.js';

const { t, locale } = useWasloI18n();
const route = useRoute();
const accountId = useWasloAccountId();

const positiveInt = value => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const broadcastId = computed(() => positiveInt(route?.params?.broadcastId));

const broadcast = useWasloApi(
  signal => wasloApi.getBroadcast(accountId.value, broadcastId.value, signal),
  { immediate: false }
);

const row = computed(() => broadcast.data.value);
const num = value =>
  new Intl.NumberFormat(toIntlLocale(locale.value)).format(value);

const load = () => {
  if (broadcastId.value !== null) broadcast.reload();
};

// ── the poll ───────────────────────────────────────────────────────────────────────
const POLL_MS = 5_000;
let timer = null;

const isLive = computed(
  () => row.value?.status === 'running' || row.value?.status === 'paused'
);

const stopPolling = () => {
  if (timer !== null) {
    clearInterval(timer);
    timer = null;
  }
};

const startPolling = () => {
  stopPolling();
  if (isLive.value) timer = setInterval(load, POLL_MS);
};

watch(isLive, startPolling);
onMounted(load);
// The route reuses this component for a different id, and `<keep-alive>` means neither a
// remount nor `onMounted` fires. Without this, opening a second broadcast from the list
// would show the first one's numbers under the second one's URL.
watch(broadcastId, load);
onActivated(() => {
  load();
  startPolling();
});
onDeactivated(stopPolling);
onUnmounted(stopPolling);

const listUrl = computed(() =>
  accountId.value === null
    ? null
    : frontendURL(`accounts/${accountId.value}/campaigns/broadcasts`)
);

const errorMessage = computed(() => {
  const error = broadcast.error.value;
  if (!error) return '';
  return error.code === WASLO_ERROR_CODES.NOT_FOUND
    ? t('WASLO.BROADCASTS.DETAIL.NOT_FOUND')
    : t('WASLO.COMMON.ERROR_BODY');
});

/**
 * THE SIX NUMBERS, AND WHY PENDING SITS BETWEEN SENT AND ARRIVED.
 *
 * `sentCount` is `sent + delivered + read` and `pendingCount` is `accepted` — dispatched,
 * and no provider has said anything about it yet. They are DISJOINT, and with skipped and
 * did-not-arrive they close the total exactly. Before the split, `accepted` was counted as
 * sent, which reported success for messages that had not been delivered; after it, a tile
 * for `sent` with no sibling for `pending` leaves the same hole in the other direction —
 * a finished, healthy run showing 4 of 812 and no account of the rest.
 *
 * It is NOT a double count of the `accepted` chip in the breakdown below, and the two are
 * given the same words on purpose. The chips are the LIVE per-status truth, recomputed
 * from the recipient rows on every read; these six are the counters frozen at the last
 * finalise. On a finished run whose receipts are still trickling in they legitimately
 * differ — the same way `SENT` here and the `sent`/`delivered`/`read` chips already do —
 * and one name across both is what lets a reader see that they are the same bucket
 * rather than two of them.
 */
const tiles = computed(() => {
  if (!row.value) return [];
  return [
    { key: 'RECIPIENTS', value: row.value.recipientCount },
    { key: 'SKIPPED', value: row.value.excludedCount },
    { key: 'SENT', value: row.value.sentCount },
    { key: 'PENDING', value: row.value.pendingCount },
    { key: 'DELIVERED', value: row.value.deliveredCount },
    { key: 'FAILED', value: row.value.failedCount },
  ].map(tile => ({
    key: tile.key,
    label: t(`WASLO.BROADCASTS.DETAIL.NUMBERS.${tile.key}`),
    value: num(tile.value),
  }));
});

// ── what can be done about it ──────────────────────────────────────────────────────
const canCancel = computed(
  () => row.value?.status === 'running' || row.value?.status === 'paused'
);
const canResume = computed(() => row.value?.status === 'paused');
const canRetry = computed(
  () =>
    row.value?.status === 'running' ||
    (row.value?.status === 'completed' &&
      (row.value?.progress?.failed ?? 0) > 0)
);
const hasActions = computed(
  () => canCancel.value || canResume.value || canRetry.value
);

const confirming = ref('');
const isActing = ref(false);
const actionError = ref('');

const ACTIONS = {
  cancel: id => wasloApi.cancelBroadcast(accountId.value, id),
  resume: id => wasloApi.resumeBroadcast(accountId.value, id),
  retry: id => wasloApi.retryBroadcast(accountId.value, id),
};

/**
 * THE ONE 409 THAT IS NOT A RACE, and the reason "Send again" needs a sentence of its own
 * rather than a truer gate.
 *
 * The other conflicts on these three actions are two tabs disagreeing — somebody stopped
 * it first — and the sidecar's own wording ("broadcast is cancelled, not running or
 * paused") is about as useful as anything this side could write. This one is different:
 * it is the EXPECTED answer for a broadcast that has failures the retry is deliberately
 * unwilling to re-send, because those recipients kept their message id and re-sending
 * would charge for them twice. Nothing about the run is wrong and nothing is broken, so
 * the raw upstream clause — English, in an Arabic page — is the wrong thing to show for
 * it.
 *
 * WHY THIS IS MATCHED ON PROSE, which this section otherwise refuses to do (see
 * STOP_SENTENCES below, written precisely to stop reading server text). Because there is
 * nothing else to match on: the refusal is a plain 409, and `BroadcastRowDTO` carries no
 * retryable count — `failedCount` and `progress.failed` BOTH include the recipients the
 * sidecar will not re-send, so no arithmetic on this side can tell the two cases apart.
 * The match is therefore a best effort with a safe floor: if the sidecar rewords, this
 * stops matching and the message falls back to exactly what shipped before, not to a
 * fabricated one. Pinned in `specs/broadcasts.spec.js`; the sentence itself lives in
 * `services/ai-sidecar/src/http/broadcasts.ts`.
 */
const NOTHING_TO_RETRY = /no failed recipients that can be re-?sent/i;

const refusalMessage = (which, error) => {
  if (error?.status !== 409) return t('WASLO.BROADCASTS.DETAIL.ACTIONS.FAILED');
  if (which === 'retry' && NOTHING_TO_RETRY.test(error.message ?? '')) {
    return t('WASLO.BROADCASTS.DETAIL.ACTIONS.NOTHING_TO_RETRY');
  }
  // The sidecar's own sentence: another tab stopped it, or started it, first.
  return error.message;
};

const run = async which => {
  if (isActing.value || broadcastId.value === null) return;
  isActing.value = true;
  actionError.value = '';
  try {
    // Render from the response: each of these answers with the broadcast AS IT IS NOW
    // rather than with what it just did, so the chip and the counts cannot disagree.
    broadcast.data.value = await ACTIONS[which](broadcastId.value);
    confirming.value = '';
  } catch (error) {
    actionError.value = refusalMessage(which, error);
    // A 409 is a DECISION, not a hiccup: pressing Yes again gets the same refusal, and
    // "Send again to up to 4 customers?" left standing under "None of these can be sent
    // again" is a screen arguing with itself. Anything else may well succeed on a second
    // press, so the question stays where it was.
    if (error?.status === 409) confirming.value = '';
  } finally {
    isActing.value = false;
  }
};

/**
 * "It stopped because: <sentence>", in three nodes rather than one.
 *
 * THE SIDECAR SENDS A CODE, NOT PROSE, and this maps it. It used to send its own message,
 * which for the two paths that actually stop a run was a raw upstream failure — an owner
 * mid-send was shown `chatwoot POST /api/v1/accounts/3/… -> 401`, in English, inside the
 * Arabic sentence below. The server emits conditions; this file emits sentences, because
 * only this side knows which language she reads.
 *
 * Anything unrecognised — including the free text still stored on rows from before the
 * change — falls to UNKNOWN. The one thing this must never do is print the field.
 *
 * The bidi isolate stays: the sentence is interpolated into a frame that carries a colon,
 * and without a real isolate an Arabic reason inside an English frame swallows that colon
 * and moves it, mangling the one line that explains why a paid send stopped.
 */
const STOP_SENTENCES = {
  send_auth_failed: 'WASLO.BROADCASTS.DETAIL.STOPPED.SEND_AUTH_FAILED',
  launch_failed: 'WASLO.BROADCASTS.DETAIL.STOPPED.LAUNCH_FAILED',
  // Field report G2.1: the sidecar's `BROADCAST_STOP.OUTSIDE_SENDING_HOURS`. Not a fault.
  outside_sending_hours:
    'WASLO.BROADCASTS.DETAIL.STOPPED.OUTSIDE_SENDING_HOURS',
};

const stoppedBecause = computed(() => {
  const code = row.value?.error ?? '';
  if (!code) return null;
  const key = STOP_SENTENCES[code] ?? 'WASLO.BROADCASTS.DETAIL.STOPPED.UNKNOWN';
  return isolateParts(
    t('WASLO.BROADCASTS.DETAIL.STOPPED_BECAUSE', { reason: MARKER }),
    t(key)
  );
});

const confirmCopy = computed(() => {
  if (confirming.value === 'cancel') {
    return {
      title: t('WASLO.BROADCASTS.DETAIL.ACTIONS.CANCEL_CONFIRM'),
      body: t('WASLO.BROADCASTS.DETAIL.ACTIONS.CANCEL_BODY'),
    };
  }
  if (confirming.value === 'resume') {
    return {
      title: t('WASLO.BROADCASTS.DETAIL.ACTIONS.RESUME_CONFIRM'),
      body: t('WASLO.BROADCASTS.DETAIL.ACTIONS.RESUME_BODY'),
    };
  }
  if (confirming.value === 'retry') {
    return {
      title: t('WASLO.BROADCASTS.DETAIL.ACTIONS.RETRY_CONFIRM', {
        count: num(row.value?.progress?.failed ?? row.value?.failedCount ?? 0),
      }),
      body: t('WASLO.BROADCASTS.DETAIL.ACTIONS.RETRY_BODY'),
    };
  }
  return null;
});
</script>

<template>
  <WasloPageLayout
    :header-title="row ? row.title : t('WASLO.BROADCASTS.TITLE')"
    :back-url="listUrl"
    :back-label="t('WASLO.BROADCASTS.DETAIL.BACK')"
    :is-fetching="broadcast.isFetching.value"
    :error-message="errorMessage"
    @retry="load"
  >
    <template #body>
      <div v-if="row" class="flex flex-col gap-8 pb-10">
        <!-- 1 · WHAT STATE IS IT IN -->
        <section class="flex flex-col gap-3" data-waslo-broadcast-detail>
          <div class="flex flex-wrap items-center gap-3">
            <BroadcastStatusChip :status="row.status" />
            <Button
              :label="t('WASLO.BROADCASTS.DETAIL.REFRESH')"
              variant="faded"
              color="slate"
              size="sm"
              icon="i-lucide-refresh-cw"
              data-waslo-broadcast-refresh
              @click="load"
            />
          </div>
          <p
            v-if="row.status === 'unknown'"
            class="mb-0 text-sm text-n-amber-11 max-w-[68ch]"
          >
            {{ t('WASLO.BROADCASTS.UNKNOWN_NOTE') }}
          </p>
          <!-- Three nodes, never one string: the middle one is the bidi isolate. -->
          <p
            v-if="stoppedBecause"
            class="mb-0 text-sm text-n-ruby-9 max-w-[68ch]"
            dir="auto"
            data-waslo-broadcast-error
          >
            <span>{{ stoppedBecause.before }}</span
            ><bdi>{{ stoppedBecause.value }}</bdi
            ><span>{{ stoppedBecause.after }}</span>
          </p>
        </section>

        <!-- 2 · THE NUMBERS — six, so two rows of three rather than a five-wide row
             with one tile orphaned under it. -->
        <section class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <StatTile
            v-for="tile in tiles"
            :key="tile.key"
            :label="tile.label"
            :value="tile.value"
            :data-waslo-stat="tile.key"
          />
        </section>

        <!-- 3 · WHAT WENT OUT -->
        <section v-if="row.bodyPreview" class="flex flex-col gap-2">
          <h2 class="mb-0 text-sm font-medium text-n-slate-12">
            {{ t('WASLO.BROADCASTS.DETAIL.SENT_MESSAGE') }}
          </h2>
          <p
            class="px-4 py-3 mb-0 text-sm leading-6 whitespace-pre-line outline outline-1 -outline-offset-1 outline-n-weak rounded-xl bg-n-solid-1 text-n-slate-12"
            dir="auto"
            data-waslo-broadcast-body
          >
            {{ row.bodyPreview }}
          </p>
        </section>

        <!-- 4 · WHERE EACH MESSAGE GOT TO -->
        <section class="flex flex-col gap-4">
          <SettingsHeader
            :heading="t('WASLO.BROADCASTS.DETAIL.PROGRESS.HEADING')"
            :description="t('WASLO.BROADCASTS.DETAIL.PROGRESS.DESCRIPTION')"
          />
          <ProgressBreakdown :progress="row.progress" />
        </section>

        <!-- 5 · WHAT YOU CAN DO ABOUT IT -->
        <section class="flex flex-col gap-4" data-waslo-broadcast-actions>
          <SettingsHeader
            :heading="t('WASLO.BROADCASTS.DETAIL.ACTIONS.HEADING')"
            :description="
              hasActions ? '' : t('WASLO.BROADCASTS.DETAIL.ACTIONS.NONE')
            "
          />

          <p
            v-if="actionError"
            class="mb-0 text-sm text-n-ruby-9 max-w-[68ch]"
            role="alert"
            data-waslo-broadcast-action-error
          >
            {{ actionError }}
          </p>

          <!-- The question replaces the buttons where they stood, so the counts it is
               about are still on screen above it. -->
          <div
            v-if="confirmCopy"
            class="flex flex-col gap-3 p-4 outline outline-1 -outline-offset-1 outline-n-ruby-8 rounded-xl bg-n-solid-1"
            data-waslo-broadcast-confirm
          >
            <p class="mb-0 text-sm font-medium text-n-slate-12">
              {{ confirmCopy.title }}
            </p>
            <p class="mb-0 text-sm text-n-slate-11 max-w-[62ch]">
              {{ confirmCopy.body }}
            </p>
            <div class="flex flex-wrap items-center gap-3">
              <Button
                :label="t('WASLO.BROADCASTS.DETAIL.ACTIONS.CONFIRM_YES')"
                variant="solid"
                color="ruby"
                size="sm"
                :is-loading="isActing"
                :disabled="isActing"
                data-waslo-broadcast-confirm-yes
                @click="run(confirming)"
              />
              <Button
                :label="t('WASLO.BROADCASTS.DETAIL.ACTIONS.CONFIRM_NO')"
                variant="faded"
                color="slate"
                size="sm"
                :disabled="isActing"
                @click="confirming = ''"
              />
            </div>
          </div>

          <div v-else-if="hasActions" class="flex flex-wrap items-center gap-3">
            <Button
              v-if="canCancel"
              :label="t('WASLO.BROADCASTS.DETAIL.ACTIONS.CANCEL')"
              variant="faded"
              color="ruby"
              size="sm"
              data-waslo-broadcast-cancel
              @click="confirming = 'cancel'"
            />
            <Button
              v-if="canResume"
              :label="t('WASLO.BROADCASTS.DETAIL.ACTIONS.RESUME')"
              variant="faded"
              color="slate"
              size="sm"
              data-waslo-broadcast-resume
              @click="confirming = 'resume'"
            />
            <Button
              v-if="canRetry"
              :label="t('WASLO.BROADCASTS.DETAIL.ACTIONS.RETRY')"
              variant="faded"
              color="slate"
              size="sm"
              data-waslo-broadcast-retry
              @click="confirming = 'retry'"
            />
          </div>
        </section>
      </div>
    </template>
  </WasloPageLayout>
</template>
